/**
 * Context-matched word senses (docs/plans/localization.md, "Context-matched word senses").
 *
 * The story payload's `wordSenses` says, for some word occurrences, which sense of the word's
 * inventory (`senses` on word payloads) it has in its sentence. Each entry carries `at`: how many
 * non-whitespace characters of the story text come before the word. We walk the rendered words
 * the same way `attachSentences` does (tags stripped, whitespace ignored), so the backend's
 * `</p><p>` cuts do not matter, and an entry is attached only when the word at that position is
 * the same word. Anything else is ignored: the reader then shows the word as it does today.
 */

export interface WordSenseEntry {
  at: number
  paragraph: number
  offset: number
  word: string
  /** 0-based index into the word's `senses`. */
  sense: number
  pinyin: string
  /** The pinyin to show above the word, or null to keep the segmentation pinyin. */
  textPinyin: string | null
}

export interface OccurrenceSense {
  word: string
  sense: number
  pinyin: string
  textPinyin: string | null
}

export interface Sense {
  pinyin: string
  en: string
}

const TAG_REGEX = /<[^>]+>/g
const WHITESPACE_REGEX = /\s/

/** DOM word id (`WORD-<p><i>`, as LeftStory numbers words) to the sense of that occurrence. */
export function attachWordSenses(
  paragraphs: { word: string }[][],
  wordSenses: WordSenseEntry[] | null | undefined,
): Map<string, OccurrenceSense> {
  const out = new Map<string, OccurrenceSense>()
  if (!Array.isArray(wordSenses) || !wordSenses.length) return out
  const byPosition = new Map<number, WordSenseEntry>()
  for (const entry of wordSenses) {
    if (entry && Number.isInteger(entry.at) && Number.isInteger(entry.sense)) byPosition.set(entry.at, entry)
  }

  let pos = 0
  for (let p = 0; p < paragraphs.length; p++) {
    for (let i = 0; i < paragraphs[p].length; i++) {
      const raw = (paragraphs[p][i]?.word || '').replace(TAG_REGEX, '')
      let first = -1
      for (const char of raw) {
        if (WHITESPACE_REGEX.test(char)) continue
        if (first < 0) first = pos
        pos++
      }
      if (first < 0) continue
      const entry = byPosition.get(first)
      if (entry && entry.word === raw.trim()) {
        out.set(`WORD-${p}${i}`, { word: entry.word, sense: entry.sense, pinyin: entry.pinyin, textPinyin: entry.textPinyin ?? null })
      }
    }
  }
  return out
}

const present = (value: unknown): value is string => typeof value === 'string' && value.trim() !== ''

/** A word payload's inventory, or null when it has none (the panel then shows today's meaning). */
export function wordSenseList(wordDetails: any): Sense[] | null {
  const senses = wordDetails?.senses
  if (!Array.isArray(senses) || !senses.length) return null
  const clean = senses.filter((sense: any) => present(sense?.en)).map((sense: any) => ({ pinyin: sense.pinyin || '', en: sense.en }))
  return clean.length === senses.length ? clean : null
}

/**
 * The order the panel lists senses in: the context-picked one first, then the others in inventory
 * order. Without a pick (or an out-of-range one), inventory order.
 */
export function senseOrder(count: number, picked: number | null | undefined): number[] {
  const order = Array.from({ length: count }, (_, i) => i)
  if (picked == null || picked < 0 || picked >= count) return order
  return [picked, ...order.filter((i) => i !== picked)]
}

/**
 * The learner-language renderings aligned with the inventory: the stored `localizedMeaning` is one
 * rendering per sense joined with '; '. Null when the count does not match (then the panel shows
 * the localized meaning as a whole, as it does without an inventory).
 */
export function alignedRenderings(localizedMeaning: unknown, count: number): string[] | null {
  if (!present(localizedMeaning)) return null
  const parts = localizedMeaning.split('; ').map((part) => part.trim())
  return parts.length === count && parts.every(Boolean) ? parts : null
}
